"use client";

import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { ComponentProps } from "react";
import { twMerge } from "tailwind-merge";

import { OrientedUrl, urlOrientedMap } from "@/data/constants/oriented";
import { links } from "@/data/links";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { Button } from "ui/core/button";

import { SymbolInfo } from "../../services/symbol-info";
import SlidesOpenButton from "./SlidesOpenButton";

type SymbolTitleProps = {
  symbol: string;
  lang: Lang;
  data: SymbolInfo;
};

export const SymbolTitle = ({ symbol, lang, data }: SymbolTitleProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const { t } = getTranslations(lang);

  const isHasQuartrSlides = data.slideExists;

  const orientedUrl = pathname.split("/")[5];
  const oriented = orientedUrl
    ? urlOrientedMap[orientedUrl as OrientedUrl]
    : "overview";
  const orientedName = t.analysis.shared.oriented[oriented];

  return (
    <section className="relative flex items-start justify-between gap-2 px-4 @lg:px-0 sm:gap-6">
      <div className="flex flex-wrap gap-2">
        <h1 className="flex flex-col gap-2" aria-label="company name">
          <span className="text-2xl font-bold text-white @3xl:text-3xl">
            {data.name}{" "}
          </span>
          <span className="text-sm text-zinc-400">{`${symbol}.${data.country?.toUpperCase()} ${orientedName}${
            t.analysis.analysis
          }`}</span>
        </h1>
        <div className="flex h-fit gap-2 pt-2">
          <Badge aria-label="market">
            {t.analysis.overview.stock(data.country ?? "US")}
          </Badge>
          <Badge aria-label="category">{data.mainCategory}</Badge>
        </div>
      </div>
      <div className="mb-[-20px] flex shrink-0 gap-2 self-end">
        <div>
          <Button
            variant="secondary"
            aria-label="Open TradingView Chart"
            className="flex items-center gap-1 px-1 text-sky-700 sm:gap-2 sm:px-4"
            onClick={() =>
              router.push(
                `/${lang}${links.myAnalysisTradingViewAdvancedChart({
                  symbol,
                })}`
              )
            }
          >
            <Image
              src="/tradingview/logo.svg"
              alt="TradingView Logo"
              width={25}
              height={19}
            />
            {t.analysis.viewChart}
          </Button>
        </div>
        <div>
          <SlidesOpenButton
            symbol={symbol}
            isHasQuartrSlides={isHasQuartrSlides ?? false}
            lang={lang}
          />
        </div>
      </div>
    </section>
  );
};

const Badge = (props: ComponentProps<"span">) => (
  <span
    {...props}
    className={twMerge(
      "whitespace-nowrap rounded-full border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600",
      props.className
    )}
  >
    {props.children}
  </span>
);
